import { Observable, throwError } from 'rxjs'
import { ajax, AjaxResponse } from 'rxjs/ajax'
import { catchError, map } from 'rxjs/operators'
import { env } from 'shared/env'

import { ILoginPayload, ISignupPayload } from 'client/../decl'

const baseURL = env.SERVER_ADDR

const requestHeader = () => {
  const headers = {
    'Content-Type': 'application/json',
  }
  const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
  let token: string | boolean = false

  if (userData.token) {
    token = userData.token
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

interface ErrorResponse {
  status: number
  message: string
}

const handleError = (operation: string) => (err: ErrorResponse) => {
  const errMsg = `Error in ${operation}()`

  if (err.status === 401) {
    const currentPath = window.location.pathname || ''
    // Avoid forcing redirect during auth flows (signup, otp, login)
    if (!currentPath.startsWith('/auth/')) {
      localStorage.clear()
      window.location.href = '/auth/login'
    }
  }
  return throwError(() => console.error(errMsg))
}

export interface IUser {
  id: string
  email: string
  name: string
  token: string
  is_otp_verified?: boolean
  permissions?: string[]
}

export interface ITenant {
  id: string
  store_id: string
  name: string
}

export interface ILoginResponse {
  user: IUser
  tenant: ITenant
  error?: string
  field?: string
  data?: Record<string, string | number | boolean | null>
  token?: string
}
export interface ISignupResponse {
  user: IUser
  business: ITenant
  error?: string
  field?: string
  data?: Record<string, string | number | boolean | null>
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
}

export interface VerifyResetPasswordResponse {
  validated?: boolean
  error?: string
  message?: string
}

export const verifyResetPasswordToken = (
  token: string,
): Observable<AjaxResponse<VerifyResetPasswordResponse>> =>
  ajax<VerifyResetPasswordResponse>({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/auth/password/verify?token=${token}`,
  }).pipe(catchError(handleError('verifyResetPasswordToken')))

export const verifyResetPassword = (payload: {
  token: string
  password: string
}): Observable<AjaxResponse<VerifyResetPasswordResponse>> =>
  ajax<VerifyResetPasswordResponse>({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/auth/password/reset`,
    body: payload,
  }).pipe(catchError(handleError('verifyResetPassword')))

export const login = (payload: ILoginPayload): Observable<ILoginResponse> => {
  return ajax<ILoginResponse>({
    url: `${baseURL}api/v2/auth/login`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload,
  }).pipe(map((res) => res.response))
}

export const signup = (payload: ISignupPayload) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/auth/signup`,
    body: payload,
  }).pipe(catchError(handleError('signup')))

export const resetPassword = (
  email: string,
): Observable<ResetPasswordResponse> => {
  return ajax<ResetPasswordResponse>({
    url: '/api/v2/auth/reset-password',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: { email },
  }).pipe(map((res) => res.response))
}

export const signupOtp = (payload: { otp: string }) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/auth/verify-otp`,
    body: payload,
  }).pipe(catchError(handleError('signupOtp')))

export const resendOtp = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/auth/regenerate-otp`,
  }).pipe(catchError(handleError('resendOtp')))
